// Watchlist data layer — persistent storage for tracked products, alerts, and saved decisions

const WATCHLIST_KEY = "decision-os-watchlist";
const HISTORY_KEY = "decision-os-history";

export interface WatchlistItem {
  id: string;
  productName: string;
  query: string;
  category: string;
  label: string; // Recommended, Best Value, etc.
  price: string;
  priceNumeric: number;
  deliveryTime: string;
  confidence: string;
  coverage: string;
  type: "save" | "track" | "alert";
  autoBuy?: { maxPrice?: number; maxDeliveryDays?: number; trustedOnly?: boolean };
  addedAt: string;
  // Simulated changes
  priceChange?: number; // percentage, negative = drop
  signal?: string; // "Price likely to drop", etc.
  notification?: string; // "Price dropped 12%", etc.
}

export interface HistoryEntry {
  id: string;
  query: string;
  category: string;
  country?: string;
  winnerName: string;
  winnerPrice: string;
  winnerLabel: string;
  confidence: string;
  coverage: string;
  decidedAt: string;
  receipt?: string; // serialized receipt data
}

// ─── Watchlist ───────────────────────────────────────────────────────────────

function getWatchlist(): WatchlistItem[] {
  try {
    const raw = localStorage.getItem(WATCHLIST_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveWatchlist(items: WatchlistItem[]): void {
  try {
    localStorage.setItem(WATCHLIST_KEY, JSON.stringify(items));
  } catch {}
}

export function addToWatchlist(item: Omit<WatchlistItem, "id" | "addedAt">): WatchlistItem {
  const items = getWatchlist();
  // Don't duplicate same product + type
  const exists = items.find((i) => i.productName === item.productName && i.type === item.type);
  if (exists) return exists;

  const newItem: WatchlistItem = {
    ...item,
    id: `wl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    addedAt: new Date().toISOString(),
  };


  items.unshift(newItem);
  saveWatchlist(items);
  return newItem;
}

export function removeFromWatchlist(id: string): void {
  const items = getWatchlist().filter((i) => i.id !== id);
  saveWatchlist(items);
}

export function getWatchlistItems(): WatchlistItem[] {
  return getWatchlist();
}

export function updateAutoBuy(
  id: string,
  autoBuy: WatchlistItem["autoBuy"]
): void {
  const items = getWatchlist();
  const item = items.find((i) => i.id === id);
  if (item) {
    item.autoBuy = autoBuy;
    saveWatchlist(items);
  }
}

export function getWatchlistCount(): number {
  return getWatchlist().length;
}

export function getActiveAlerts(): WatchlistItem[] {
  return getWatchlist().filter((i) => i.type === "alert" || i.notification);
}

export function getNotifications(): WatchlistItem[] {
  return getWatchlist().filter((i) => i.notification);
}

// ─── History ─────────────────────────────────────────────────────────────────

function getHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHistory(entries: HistoryEntry[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, 50)));
  } catch {}
}

export function addToHistory(entry: Omit<HistoryEntry, "id" | "decidedAt">): void {
  const entries = getHistory();
  entries.unshift({
    ...entry,
    id: `hist-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    decidedAt: new Date().toISOString(),
  });
  saveHistory(entries);
}

export function getHistoryEntries(): HistoryEntry[] {
  return getHistory();
}

export function clearHistory(): void {
  saveHistory([]);
}

// ─── Activity Summary ────────────────────────────────────────────────────────

export function getActivitySummary() {
  const history = getHistory();
  const watchlist = getWatchlist();
  const tracked = watchlist.filter((i) => i.type === "track");
  const alerts = watchlist.filter((i) => i.type === "alert");
  const drops = watchlist.filter((i) => i.priceChange && i.priceChange < 0);

  // Estimate savings based on simulated price drops
  const estimatedSavings = drops.reduce((sum, item) => {
    return sum + Math.abs(item.priceChange || 0) * (item.priceNumeric / 100);
  }, 0);

  return {
    totalDecisions: history.length,
    productsTracked: tracked.length,
    activeAlerts: alerts.length,
    betterDealsFound: drops.length,
    estimatedSavings: Math.round(estimatedSavings),
    weeklyDecisions: history.filter((h) => {
      const d = new Date(h.decidedAt);
      const week = Date.now() - 7 * 24 * 60 * 60 * 1000;
      return d.getTime() > week;
    }).length,
  };
}

// ─── Buying Profile ──────────────────────────────────────────────────────────

export function getBuyingProfile() {
  const history = getHistory();
  if (history.length < 2) return null;

  const labels = history.map((h) => h.winnerLabel);
  const valueCount = labels.filter((l) => l === "Best Value").length;
  const speedCount = labels.filter((l) => l === "Fastest Delivery").length;
  const premiumCount = labels.filter((l) => l === "Premium Pick").length;
  const recCount = labels.filter((l) => l === "Recommended").length;

  const categories = history.map((h) => h.category);
  const topCategory = mode(categories);

  let insight = "";
  if (valueCount > recCount && valueCount > speedCount) {
    insight = "You tend to choose best value over fastest delivery.";
  } else if (speedCount > valueCount) {
    insight = "You prioritize speed over savings.";
  } else if (premiumCount >= 2) {
    insight = "You lean toward premium options when available.";
  } else {
    insight = "You balance value and reliability in your decisions.";
  }

  return {
    insight,
    topCategory,
    totalDecisions: history.length,
    valueBias: valueCount / history.length,
    speedBias: speedCount / history.length,
  };
}

function mode(arr: string[]): string {
  const counts: Record<string, number> = {};
  arr.forEach((v) => (counts[v] = (counts[v] || 0) + 1));
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
}

// ─── Progression ─────────────────────────────────────────────────────────────

export type ProgressionLevel = "New User" | "Smart Shopper" | "Deal Optimizer" | "Decision Pro" | "Operator";

export function getProgressionLevel(): { level: ProgressionLevel; progress: number; next: ProgressionLevel | null } {
  const history = getHistory();
  const watchlist = getWatchlist();
  const score = history.length * 2 + watchlist.length;

  if (score >= 30) return { level: "Operator", progress: 100, next: null };
  if (score >= 20) return { level: "Decision Pro", progress: Math.min(100, ((score - 20) / 10) * 100), next: "Operator" };
  if (score >= 12) return { level: "Deal Optimizer", progress: Math.min(100, ((score - 12) / 8) * 100), next: "Decision Pro" };
  if (score >= 5) return { level: "Smart Shopper", progress: Math.min(100, ((score - 5) / 7) * 100), next: "Deal Optimizer" };
  return { level: "New User", progress: Math.min(100, (score / 5) * 100), next: "Smart Shopper" };
}
